
import type {
  ArrayConfigFieldSchema,
  ObjectConfigFieldData,
} from '@empathyco/playboard-core-domain'
import type { PropType } from 'vue'
import type { ServiceConfigFieldValidityState } from '../../models'
import { CollapseHeight } from '@empathyco/x-components'

import { deepMerge } from '@empathyco/x-deep-merge'

import { deepEqual } from '@empathyco/x-utils'

import { defineComponent } from 'vue'

import { TYPES } from '../../config/inversify.types'

import { ListAnimationMixin } from '../../mixins'

import { InstanceManagementUtils } from '../../utils'

import { EpbButton } from '../shared/epb-button'
import InputErrors from '../shared/input-errors.component.vue'
import InputLabel from '../shared/input-label.component.vue'
import SearchBox from '../shared/search-box.component.vue'
import ObjectArrayServiceConfigFieldValueItem from './object-array-service-config-field-value-item.component.vue'

export default defineComponent({
  name: 'ObjectArrayServiceConfigField',
  components: {
    CollapseHeight,
    EpbButton,
    InputErrors,
    InputLabel,
    ObjectArrayServiceConfigFieldValueItem,
    SearchBox,
  },
  mixins: [ListAnimationMixin],
  inject: {
    objectIdService: TYPES.ObjectIdService,
  },
  props: {
    label: {
      type: String,
    },
    description: {
      type: String,
    },
    value: {
      type: Array as PropType<Array<ObjectConfigFieldData>>,
      default: () => [],
    },
    schema: {
      type: Object as PropType<ArrayConfigFieldSchema>,
      required: true,
    },
    required: {
      type: Boolean,
    },
    readonly: {
      type: Boolean,
    },
    level: {
      type: Number,
    },
    errors: {
      type: Array as PropType<Array<string>>,
      default: () => [],
    },
  },
  data(): Record<string, any> {
    return {
      baseClass: 'epb-object-array-service-config-field',
      formValueItem: undefined,
      editingValueItem: undefined,
      inputQuery: undefined,
      items: [],
    }
  },
  computed: {
    formValueItemValidityState(): ServiceConfigFieldValidityState {
      return InstanceManagementUtils.getServiceConfigFieldValidityState(
        this.schema.items,
        this.formValueItem,
      )
    },
    editValueItemValidityState(): ServiceConfigFieldValidityState {
      return InstanceManagementUtils.getServiceConfigFieldValidityState(
        this.schema.items,
        this.editingValueItem,
      )
    },
  },
  watch: {
    schema: {
      handler(schema: ArrayConfigFieldSchema): void {
        this.formValueItem = InstanceManagementUtils.getServiceConfigFieldFormData(schema.items)
        this.items = deepMerge([], this.value)
      },
      deep: true,
      immediate: true,
    },
    inputQuery: {
      handler(query: string): void {
        const valueFiltered = deepMerge([], this.value)
        if (this.inputQuery) {
          this.items = valueFiltered.filter((itemValue: Record<string, any>) =>
            Object.values(itemValue).some(
              (value: string) =>
                typeof value === 'string' && value.toLowerCase().includes(query.toLowerCase()),
            ),
          )
        } else {
          this.items = valueFiltered
        }
      },
      immediate: true,
    },
  },
  methods: {
    add(valueItem: ObjectConfigFieldData, resetForm = false): void {
      const value = this.items.slice()
      value.push(valueItem)
      this.$emit('input', value)
      if (resetForm) {
        this.formValueItem = InstanceManagementUtils.getServiceConfigFieldFormData(
          this.schema.items,
        )
      }
      this.items = value
    },
    remove(valueItem: ObjectConfigFieldData): void {
      const valueIndex = this.items.findIndex((item: ObjectConfigFieldData) =>
        deepEqual(valueItem, item),
      )
      const value = this.items.slice()
      value.splice(valueIndex, 1)
      this.$emit('input', value)
      this.items = value
    },
    accept(valueItem: ObjectConfigFieldData): void {
      const valueIndex = this.items.findIndex((item: ObjectConfigFieldData) =>
        deepEqual(valueItem, item),
      )
      const value = this.items.slice()
      value.splice(valueIndex, 1, this.editingValueItem)
      this.$emit('input', value)
      this.editingValueItem = undefined
    },
    onQueryChange(query: string): void {
      this.inputQuery = query
    },
    onClearQuery(): void {
      this.inputQuery = undefined
    },
  },
})
